import {createInteractionId, mark, types as metrics} from '@servicenow/ui-metrics';
import {produce} from 'immer';
import {
	isDeclarativeUpdate,
	isValidOperation,
	isBatch,
	resolveUpdate,
	isValidOptimisationOperation,
	shouldUpdateState
} from './utils';
import * as operations from '../operators/operations';
import getHostVariables from '../getHostVariables';
import {schedule, types} from '../../scheduler';
import deferHelperExecution from './deferHelperExecution';
import {STATE, RENDER, COMPONENT_ID, CONDITIONAL_DISPATCH, PROPS} from '../../symbols';
import {
	HELPERS_SCHEDULED,
	SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED,
	STATE_UPDATED,
	HELPERS_DEFER,
	COMPONENT_STATE_UPDATED,
	NOW_AVOID_RENDER_PROP
} from '../../constants';
import log from '../../log';
import {emitNowUiEvent, shouldOptOutOfDeepCompareForPage} from '../';
import {get, isSelectiveEqual} from '@servicenow/ui-internal';
import {getConfigItem, configConstants} from '@servicenow/ui-config';

const {ENABLE_DECLARATIVE_STATE_UPDATE_DEEP_COMPARE} = configConstants;
let optOutDeepCompare = shouldOptOutOfDeepCompareForPage('state');
const shouldDeepCompareValues = getConfigItem(ENABLE_DECLARATIVE_STATE_UPDATE_DEEP_COMPARE);
const {IMMEDIATE, STATE_SET} = types;
const {
	declarativeOperations: {ASSIGN}
} = operations;

window.addEventListener(
	'popstate',
	() => (optOutDeepCompare = shouldOptOutOfDeepCompareForPage('state'))
);

function handleStateUpdate(host, hostVariables, update, interactionId) {
	const resolvedUpdate = resolveUpdate(host, hostVariables, update, interactionId);
	const {
		shouldUpdate = true,
		operation = ASSIGN,
		path = '',
		start,
		deleteCount,
		addItems,
		shouldRender = true,
		value
	} = resolvedUpdate;

	if (!shouldUpdate || !shouldUpdateState(hostVariables[STATE], resolvedUpdate))
		return {didUpdate: false, shouldRender: false};

	// this an extremly naive implementation. it doesn't account
	// for operations like push or pop that you'd want to perform
	// before diffing the values
	if (shouldDeepCompareValues && !optOutDeepCompare && path && operation && value) {
		const nextValue = value;
		const currentValue = get(hostVariables[STATE], path);
		if (isSelectiveEqual(currentValue, nextValue)) return {didUpdate: false, shouldRender: false};
	}

	const previousState = hostVariables[STATE];
	const props = hostVariables[PROPS];
	const avoidRender = props && props[NOW_AVOID_RENDER_PROP];
	if (avoidRender && isValidOptimisationOperation(operation)) {
		try {
			const target = isDeclarativeUpdate(resolvedUpdate) ? value : resolvedUpdate;
			const source = path ? get(previousState, path) : previousState;
			if (isSelectiveEqual(source, target)) return {didUpdate: false, shouldRender: false};
		} catch (error) {
			log('Error while performing same state re-render optimisation pre-validation', {
				error,
				host,
				origin: 'handleStateUpdate'
			});
		}
	}

	const nextState = produce(hostVariables[STATE] || {}, draftState => {
		if (operation === 'replace') return value;

		if (!isValidOperation(operation)) {
			log(`Invalid update state operation: ${operation}`, {host, origin: 'handleStateUpdate'});
			return;
		}
		try {
			operations[operation](draftState, {
				path,
				value: isDeclarativeUpdate(resolvedUpdate) ? value : resolvedUpdate,
				start,
				deleteCount,
				addItems: Array.isArray(addItems) ? addItems : []
			});
		} catch (error) {
			log('Error while performing state update', {error, host, origin: 'handleStateUpdate'});
		}
	});

	if (nextState === previousState) return {didUpdate: false, shouldRender: false};

	hostVariables[STATE] = nextState; // eslint-disable-line no-param-reassign
	return {didUpdate: true, shouldRender};
}

function handleStateUpdateBatch(host, hostVariables, batch, interactionId) {
	return batch.reduce(
		(acc, update) => {
			const {shouldRender, didUpdate} = handleStateUpdate(
				host,
				hostVariables,
				update,
				interactionId
			);

			const result = {...acc};
			if (didUpdate) result.didUpdate = true;

			if (shouldRender) result.shouldRender = true;

			return result;
		},
		{didUpdate: false, shouldRender: false}
	);
}

const resolveUpdateForMark = update => (typeof update === 'function' ? 'FUNCTION' : update);
const transformUpdateForMark = (update, isBatch) => {
	if (isBatch) return update.map(resolveUpdateForMark);

	return resolveUpdateForMark(update);
};

function resolveInteractionId(interactionId, options) {
	if (interactionId) return interactionId;

	if (typeof options === 'string') return options;

	return createInteractionId();
}

export default function createUpdateState(host, executionTiming) {
	const hostVariables = getHostVariables(host);

	return function updateState(update, options = {}, interactionId) {
		const resolvedInteractionId = resolveInteractionId(interactionId, options);
		if (executionTiming === HELPERS_DEFER)
			mark(host, resolvedInteractionId, metrics.DEFER, {deferred: metrics.UPDATE_STATE});
		const meta = {el: host, interactionId: resolvedInteractionId};
		deferHelperExecution(
			executionTiming,
			() => {
				// TODO: Remove this immediate option for a better scheduling alternative
				// https://code.devsnc.com/gist/dylan-mozlowski/07bf0ab13b2c8a12f0cc6e59bf4cf911
				const {
					[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]:
						optionallyImmediate = false
				} = options;

				schedule({
					fn() {
						const resolvedUpdate = resolveUpdate(host, hostVariables, update, resolveInteractionId);
						const shouldBatch = isBatch(resolvedUpdate);

						mark(host, resolvedInteractionId, metrics.UPDATE_STATE, {
							update: transformUpdateForMark(resolvedUpdate, shouldBatch)
						});

						const previousState = hostVariables[STATE];
						const updateHandler = shouldBatch ? handleStateUpdateBatch : handleStateUpdate;
						const {didUpdate, shouldRender} = updateHandler(
							host,
							hostVariables,
							resolvedUpdate,
							resolvedInteractionId
						);
						if (!didUpdate) return;

						emitNowUiEvent(STATE_UPDATED, {
							componentId: hostVariables[COMPONENT_ID],
							previousState,
							nextState: hostVariables[STATE]
						});

						if (shouldRender) {
							host[RENDER](resolvedInteractionId, metrics.UPDATE_STATE, {
								update: transformUpdateForMark(resolvedUpdate, shouldBatch)
							});
						}
						hostVariables[CONDITIONAL_DISPATCH](
							COMPONENT_STATE_UPDATED,
							{previousState, update},
							{internal: true}
						);
					},
					meta,
					type: executionTiming !== HELPERS_SCHEDULED || optionallyImmediate ? IMMEDIATE : STATE_SET
				});
			},
			meta
		);
	};
}
